import { useEffect, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';

import { londonToday } from '../lib/gameDate';

// The puzzle drops at UK local midnight (matches the server's `today()`), so the
// countdown tracks Europe/London — not UTC.
const londonTimeFmt = new Intl.DateTimeFormat('en-GB', {
  timeZone: 'Europe/London',
  hour12: false,
  hour: '2-digit',
  minute: '2-digit',
  second: '2-digit',
});

/** Current UK calendar date as YYYY-MM-DD (shared with the rest of the app). */
export const londonDateOf = londonToday;

/** Milliseconds until the next UK wall-clock midnight. Derived from the London
 *  wall time so it always counts down to when the clock reads 00:00. */
export function msUntilNextLondonMidnight(now: Date): number {
  const parts = londonTimeFmt.formatToParts(now);
  const part = (type: string) => Number(parts.find((p) => p.type === type)?.value ?? 0);
  const hour = part('hour') % 24; // some engines render midnight as "24"
  const elapsed = hour * 3600 + part('minute') * 60 + part('second');
  return (86_400 - elapsed) * 1000 - now.getMilliseconds();
}

export function formatCountdown(ms: number): string {
  const total = Math.max(0, Math.floor(ms / 1000));
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  return [h, m, s].map((n) => String(n).padStart(2, '0')).join(':');
}

/**
 * HH:MM:SS until the next puzzle drop at 00:00 UK local time. When the UK date
 * actually rolls over while the tab is open, every query is invalidated so the
 * new day's puzzles appear without a manual refresh.
 */
export function useCountdown(): string {
  const queryClient = useQueryClient();
  const [label, setLabel] = useState(() => formatCountdown(msUntilNextLondonMidnight(new Date())));
  const dateRef = useRef(londonDateOf(new Date()));

  useEffect(() => {
    const tick = () => {
      const now = new Date();
      setLabel(formatCountdown(msUntilNextLondonMidnight(now)));
      const date = londonDateOf(now);
      if (date !== dateRef.current) {
        dateRef.current = date;
        void queryClient.invalidateQueries();
      }
    };
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [queryClient]);

  return label;
}
